import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SEO from "../../components/SEO";
import OrderCard from "../../components/OrderCard";
import ParcelOrderService from "../../services/parcel-order.service";
import AuthService from "../../services/auth.service";
import banner from "../../assets/img/delivery-illustration.webp";
import {
	BsArrowClockwise,
	BsArrowRight,
	BsBell,
	BsBoxSeam,
	BsCheckCircle,
	BsExclamationTriangle,
	BsSearch,
	BsShop,
	BsTruck,
} from "react-icons/bs";

const normaliseReference = (value) => value.replace(/[\s#-]/g, "");

function LookupStatus({ status, reference, onRetry }) {
	if (status === "loading") {
		return (
			<div className="bg-white rounded-2xl border border-purple-100 p-6 shadow-sm animate-pulse" aria-live="polite">
				<span className="sr-only">Looking up your parcel…</span>
				<div className="h-4 w-40 bg-gray-100 rounded mb-4" />
				<div className="h-3 w-full bg-gray-100 rounded mb-2" />
				<div className="h-3 w-2/3 bg-gray-100 rounded" />
			</div>
		);
	}
	if (status === "not_found") {
		return (
			<div className="bg-white rounded-2xl border-2 border-amber-200 p-6 shadow-sm" role="alert">
				<div className="flex items-start gap-3">
					<BsExclamationTriangle className="text-amber-500 text-xl flex-shrink-0 mt-0.5" aria-hidden="true" />
					<div>
						<h2 className="text-lg text-gray-900 mb-1">We couldn't find that parcel</h2>
						<p className="text-gray-600 text-sm mb-3">
							No parcel matches <span className="font-semibold text-gray-900">"{reference}"</span>. Tracking numbers are 8 digits,
							for example 48213907.
						</p>
						<ul className="text-sm text-gray-600 space-y-1 list-disc list-inside mb-4">
							<li>Check the number in your order confirmation email</li>
							<li>Receivers can use the link in their WhatsApp or SMS update</li>
						</ul>
						<a href="/contact" className="inline-flex items-center gap-1.5 text-sm font-medium text-purple-700 hover:text-purple-900">
							Still stuck? Contact us <BsArrowRight aria-hidden="true" />
						</a>
					</div>
				</div>
			</div>
		);
	}
	if (status === "error") {
		return (
			<div className="bg-white rounded-2xl border-2 border-red-200 p-6 shadow-sm" role="alert">
				<div className="flex items-start gap-3">
					<BsExclamationTriangle className="text-red-500 text-xl flex-shrink-0 mt-0.5" aria-hidden="true" />
					<div>
						<h2 className="text-lg text-gray-900 mb-1">We couldn't check right now</h2>
						<p className="text-gray-600 text-sm mb-4">Something went wrong reaching Parcelin. Please check your connection and try again.</p>
						<button
							type="button"
							onClick={onRetry}
							className="inline-flex items-center gap-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-lg transition-colors"
						>
							<BsArrowClockwise aria-hidden="true" /> Try again
						</button>
					</div>
				</div>
			</div>
		);
	}
	return null;
}

export const TrackParcelPage = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const initialReference = searchParams.get("ref") ?? "";
	const [query, setQuery] = useState(initialReference);
	const [searchedReference, setSearchedReference] = useState("");
	const [status, setStatus] = useState("idle"); // idle | loading | found | not_found | error
	const [order, setOrder] = useState(null);
	const [inputError, setInputError] = useState("");
	const latestLookup = useRef(0);

	const lookup = async (rawReference) => {
		const reference = normaliseReference(rawReference);
		if (!reference) {
			setInputError("Enter your tracking number.");
			return;
		}
		setInputError("");
		setSearchedReference(reference);
		setSearchParams({ ref: reference }, { replace: true });
		setStatus("loading");
		setOrder(null);

		const lookupId = latestLookup.current + 1;
		latestLookup.current = lookupId;
		try {
			const result = await ParcelOrderService.findOrder(reference);
			if (latestLookup.current !== lookupId) return;
			if (result?.id) {
				setOrder(result);
				setStatus("found");
			} else {
				setStatus("not_found");
			}
		} catch (error) {
			if (latestLookup.current !== lookupId) return;
			setStatus(error?.response?.status === 404 ? "not_found" : "error");
		}
	};

	useEffect(() => {
		if (initialReference) lookup(initialReference);
		// Only the reference in the link the page was opened with.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleSubmit = (event) => {
		event.preventDefault();
		lookup(query);
	};

	const statusGuide = [
		{ icon: <BsBoxSeam />, title: "Awaiting pickup", text: "Your order is booked and waiting to be collected from the pickup address." },
		{ icon: <BsCheckCircle />, title: "Collected", text: "We've picked up your parcel and it's being prepared for its journey." },
		{ icon: <BsTruck />, title: "En route", text: "Your parcel is travelling to its destination. The receiver gets a message when it's out for delivery." },
		{ icon: <BsShop />, title: "Ready for collection", text: "For Limpopo collection points: the parcel has arrived and is waiting to be collected." },
		{ icon: <BsCheckCircle />, title: "Delivered", text: "The parcel has reached the receiver, who gets a delivery confirmation message." },
	];

	return (
		<div className="w-full min-h-screen flex flex-col bg-gradient-to-br from-cyan-100 to-fuchsia-200">
			<SEO
				title="Track Your Parcel"
				description="Track your Parcelin delivery with your 8-digit tracking number and see its latest status."
				path="/track-parcel"
			/>

			<section className="max-w-6xl mx-auto w-full px-4 sm:px-5 py-12 md:py-16 grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
				<div className="lg:col-span-3 flex flex-col gap-6">
					<div>
						<p className="text-sm font-medium uppercase tracking-widest text-purple-600 mb-3">Track a parcel</p>
						<h1 className="text-4xl md:text-5xl text-purple-800 mb-4">Where's my parcel?</h1>
						<p className="text-lg text-gray-700">Enter your tracking number to see the latest status of your delivery.</p>
					</div>

					<form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-purple-100 shadow-lg p-4 sm:p-5" noValidate>
						<label htmlFor="reference_number" className="block text-sm font-medium text-gray-700 mb-2">
							Tracking number
						</label>
						<div className="flex flex-col sm:flex-row gap-3">
							<input
								id="reference_number"
								type="text"
								inputMode="numeric"
								autoComplete="off"
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								placeholder="e.g. 48213907"
								aria-invalid={Boolean(inputError)}
								aria-describedby="reference_hint"
								className="flex-1 min-w-0 px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl text-lg tracking-wide focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-colors"
							/>
							<button
								type="submit"
								disabled={status === "loading"}
								className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white font-semibold rounded-xl transition-colors"
							>
								<BsSearch aria-hidden="true" />
								{status === "loading" ? "Tracking…" : "Track"}
							</button>
						</div>
						<p id="reference_hint" className={`text-sm mt-2 ${inputError ? "text-red-600" : "text-gray-500"}`}>
							{inputError || "Your 8-digit tracking number is in your order confirmation email."}
						</p>
					</form>

					{status === "found" && order && (
						<div aria-live="polite">
							<p className="text-sm font-medium uppercase tracking-wide text-gray-500 mb-3">Latest status</p>
							<OrderCard order={order} defaultOpen />
						</div>
					)}
					<LookupStatus status={status} reference={searchedReference} onRetry={() => lookup(searchedReference)} />

					{AuthService.isLogin() && status === "idle" && (
						<a href="/parcels" className="inline-flex items-center gap-2 self-start text-sm font-medium text-purple-700 hover:text-purple-900">
							See all your parcels in My Parcels <BsArrowRight aria-hidden="true" />
						</a>
					)}
				</div>

				<div className="hidden lg:flex lg:col-span-2 justify-center">
					<img className="w-full max-w-sm h-auto object-contain drop-shadow-xl" src={banner} width="904" height="942" alt="" />
				</div>
			</section>

			<section className="bg-white">
				<div className="max-w-6xl mx-auto px-4 sm:px-5 py-14">
					<div className="max-w-3xl mb-8">
						<p className="text-sm font-medium uppercase tracking-widest text-purple-600 mb-3">Tracking explained</p>
						<h2 className="text-3xl text-gray-900">What each status means</h2>
					</div>
					<ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
						{statusGuide.map((step, index) => (
							<li key={step.title} className="relative bg-purple-50/60 rounded-2xl border border-purple-100 p-5">
								<span className="absolute top-4 right-4 text-xs font-medium text-purple-300">0{index + 1}</span>
								<span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-purple-600 text-lg mb-4 shadow-sm" aria-hidden="true">
									{step.icon}
								</span>
								<h3 className="text-base text-gray-900 mb-1">{step.title}</h3>
								<p className="text-sm text-gray-600 leading-relaxed">{step.text}</p>
							</li>
						))}
					</ol>

					<div className="mt-8 flex flex-col md:flex-row md:items-center gap-4 rounded-2xl bg-gradient-to-br from-purple-700 to-fuchsia-600 text-white p-6">
						<BsBell className="text-2xl flex-shrink-0" aria-hidden="true" />
						<p className="flex-1 text-white/90">
							<span className="font-semibold text-white">Receivers are kept in the loop.</span> They get a WhatsApp message (or SMS) when the
							parcel is out for delivery and again when it's delivered, with a link back to this page.
						</p>
						<a href="/contact" className="inline-flex items-center gap-2 self-start md:self-auto bg-white text-purple-700 hover:bg-purple-50 font-semibold px-5 py-2.5 rounded-full transition-colors">
							Need help? <BsArrowRight aria-hidden="true" />
						</a>
					</div>
				</div>
			</section>
		</div>
	);
};
